import { LitElement, html, css } from "lit";
import "@matter/matter-image/dist/matter-image";
import ResizeObserver from "resize-observer-polyfill";

const ro = new ResizeObserver((entries) => {
  entries.forEach((entry) => entry.target.resizedCallback(entry.contentRect));
});

class MatterGallery extends LitElement {
  static get styles() {
    return css`
      :host {
        display: block;
        font-family: Source Sans Pro, sans-serif;

        --main-hover: #0b4c1a;
        --main: #087021;
        --main-help: #c4ddca;
        --main-specific-help: #f7fbf8;
        --main-backgroung: #ffffff;

        --grey-main: #333333;
        --grey-help: #bebebe;
        --grey-container: #eeeeee;
        --grey-specific-help: #f5f5f5;

        --status-success: #2abc67;
        --status-info: #5bc0de;
        --status-warning: #f0ad4e;
        --status-error: #d9534f;

        --graph-1: #77b5c3;
        --graph-2: #4863a3;
        --graph-3: #9371ac;
        --graph-4: #d87d7d;
        --graph-5: #d9a46c;
        --graph-6: #e3cd66;

        --theme: #087021;
        --gray: #cacaca;
        --darkgray: #333;
      }

      matter-gallery:focus, matter-image:focus{
        outline: 0 !important;
      }

      .container {
        max-height: 500px;
      }

      .top-section {
        max-height: 300px;
        height: 60%;
        overflow: hidden;
      }

      .middle-section {
        background-color: var(--darkgray);
        margin-bottom: 15px;
        padding: 10px;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }

      .title {
        font-size: 16px;
        color: var(--gray);
        font-family: Source Sans Pro;
        text-decoration: none;
        display: inline-block;
      }

      .bottom-section {
        margin-top: 3%;
        display: flex;
        align-items: center;
        height: 40%;
        max-height: 200px;
      }

      .arrow {
        cursor: pointer;
        font-size: 22px;
      }
      .prev {
        margin-right: 10px;
      }

      .next {
        margin-left: 10px;
      }

      .inactive {
        cursor: not-allowed;
        color: var(--gray);
      }

      .wrapper {
        width: 100%;
        height: 100%;
        overflow: hidden;
      }

      .carousel {
        width: auto;
        margin: 0;
        padding: 5px;
        list-style: none;
        display: flex;
        transition: all 1s ease;
        flex-wrap: nowrap;
      }

      .element {
        flex: 1 0 18%;
        margin-left: 1%;
        margin-right: 1%;
        max-width: 25%;
      }

      .selected {
        cursor: default;
      }

      .link {
        display: table-cell;
        cursor: pointer;
      }

      .link:active {
        border-left: 3px solid transparent;
        border-right: 3px solid transparent;
        border-top: 3px solid transparent;
      }

      @media (max-width: 920px) {
        .element {
          flex: 1 0 23.5%;
        }
      }

      @media (max-width: 720px) {
        .element {
          flex: 1 0 33%;
        }
      }

      @media (max-width: 420px) {
        .arrow {
          display: none;
        }

        .element {
          flex: 1 0 23%;
        }

        .carousel {
          overflow-x: auto;
          padding-bottom: 10px;
        }
      }
    `;
  }

  static get properties() {
    return {
      endpointUrl: { type: String },
      images: { type: Array },
      inputTitle: { type: String },
      inputImageURL: { type: String },
      inputAlt: { type: String },
      inputLink: { type: String },

      _initial: { type: Number },
      _offset: { type: Number },
      _response: { type: Array },
    };
  }

  constructor() {
    super();
    this.endpointUrl = "";
    this.images = [];
    this.inputTitle = "";
    this.inputImageURL = "";
    this.inputAlt = "";
    this.inputLink = "";

    this._initial = 0;
    this._offset = 0;
    this._response = [];
    this._error = "";
  }

  render() {
    return html`
      <div class="container">
        ${this._error
          ? html` <div class="error">${this._error}</div> `
          : html`
              <div class="top-section">
                ${this._response.length > 1 ? html`
                  ${this._response.map(
                    (item, index) => html`
                      ${index == this._initial ?
                        html`
                          <a href=${item.link} target="_blank" aria-label=${item.link}>
                            <matter-image
                              src=${item.imageURL}
                              alt=${item.alt}
                              sizing="cover"
                              position="center"
                              height="100%"
                              width="100%"
                            />
                          </a>
                      `:``}
                  `)}
                `: ""}
                
              </div>
              <div class="middle-section">
              ${this._response.length > 1 ? html`
                ${this._response.map(
                  (item, index) => html`
                    ${index == this._initial ?
                      html`
                      <a
                        class="title"
                        href=${item.alt}
                        target="_blank"
                      >
                        ${item.title}
                      </a>
                    `:``}
                `)}
              `: ""}
                
              </div>
              <div class="bottom-section">
                <a
                  class="arrow prev ${this.first() ? "inactive" : ""}"
                  data-attribute="left"
                  @click=${this.slice}
                  >&#10094;</a
                >
                <div class="wrapper">
                  <ul class="carousel">
                    ${this._response.length > 1 ? html`
                    ${this._response.map(
                      (item, index) => html`
                        <li class="element">
                          <a
                            class="link ${index == this._initial
                              ? "selected"
                              : ""}"
                            data-slide="${index}"
                            @click=${this.selectImage}
                          >
                            <matter-image
                              src="${item.imageURL}"
                              alt="${item.alt}"
                              sizing="cover"
                              position="center"
                              width="100%"
                              height="100%"
                            />
                          </a>
                        </li>
                      `
                    )}
                    `: ""}
                  </ul>
                </div>
                <a
                  class="arrow next ${this.last() ? "inactive" : ""}"
                  data-attribute="right"
                  @click=${this.slice}
                  >&#10095;</a
                >
              </div>
            `}
      </div>
    `;
  }

  addFont() {
    const sourceSans = document.createElement("link");
    sourceSans.href =
      "https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@300;400;600;700&display=swap";
    sourceSans.rel = "stylesheet";
    document.head.append(sourceSans);
  }

  updated(changedProperties) {
    changedProperties.forEach((_, propName) => {
      if (["_offset"].includes(propName)) {
        this.carousel().style.transform = `translateX(${this._offset}px)`;
      }
      if (
        [
          "endpointUrl",
          "images",
          "inputTitle",
          "inputImageUrl",
          "inputAlt",
          "inputLink",
        ].includes(propName)
      ) {
        this.updatedResponse();
      }
    });
    this.addFont();
  }

  updatedResponse() {
    this._error = null;
    if (this.endpointUrl) {
      fetch(this.endpointUrl)
        .then((response) => {
          if (response.ok) {
            response.json().then((responseJSON) => {
              const elements = [];
              // Recorro los distintos objetos en busca de los atributos
              responseJSON.forEach((item) => {
                const title = item[this.inputTitle]
                  ? item[this.inputTitle]
                  : "";
                const imageURL = item[this.inputImageURL]
                  ? item[this.inputImageURL]
                  : "";
                const alt = item[this.inputAlt] ? item[this.inputAlt] : "";
                const link = item[this.inputLink] ? item[this.inputLink] : "";

                elements.push({
                  title,
                  alt,
                  link,
                  imageURL,
                });
              });

              // Añado la lista de elementos en la variable response
              this._response = elements;
            });
          } else {
            this._error = ``;
          }
        })
        .catch((error) => {
          this._error = ``;
        });
    } else {
      this._response = this.images;
    }
  }

  selectImage(e) {
    const n = e.currentTarget.getAttribute("data-slide");
    const slides = this.shadowRoot.querySelectorAll(".element");
    this._initial = Math.max(Math.min(n, slides.length), 0);
  }

  slice(e) {
    const right = e.currentTarget.getAttribute("data-attribute") === "right";
    const width = this.elementWidth();
    if ((right && !this.last()) || (!right && !this.first())) {
      this._offset += (right ? -1.1 : 1.1) * width;
    }
  }

  elementWidth() {
    return this.elements()[0].clientWidth;
  }

  elements() {
    return this.carousel().querySelectorAll(".element");
  }

  carousel() {
    return this.shadowRoot.querySelector(".carousel");
  }

  first() {
    return this._offset >= -1;
  }

  last() {
    let maxim = 0;

    try {
      maxim =
        this.carousel().offsetWidth -
        this.elementWidth() * this.elements().length;
    } catch (error) {
      return this._response.length < 4;
    }

    return this._offset <= maxim;
  }

  connectedCallback() {
    if (this._response) {
      this.updatedResponse();
    }
    super.connectedCallback();
    ro.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    ro.unobserve(this);
  }

  resizedCallback() {
    try {
      if (this.carousel().clientWidth > 405) {
        const width = this.carousel()
          .querySelectorAll(".element")
          [this._initial].getBoundingClientRect().width;
        this._offset = -this._initial * width * 1.1;
      } else {
        this._offset = 0;
        this._initial = 0;
      }
    } catch (error) {
      this._offset = 0;
      this._initial = 0;
    }
  }
}

if (!window.customElements.get("matter-gallery")) {
  window.customElements.define("matter-gallery", MatterGallery);
}
